import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Header from "../../components/header/Header";
import ProfileLayout, { PROFILE_ROUTES, useToast } from "../../components/profile/ProfileLayout";
import VerifyCard from "../../components/profile/VerifyCard";
import Field from "../../components/profile/Field";
import { IconMail, IconPhone, IconPin } from "../../components/profile/icons";
import { API, authHeaders, extractError, useKycStatus, useMe } from "../../components/profile/useProfileApi";
import "./PresonalInfo.css";

const EMPTY = { name: "", surname: "", nik: "", email: "", tel: "", country: "", city: "" };
const EMPTY_PW = { current: "", next: "", repeat: "" };

// ключ в форме -> поле в POST /users/edit
const FIELD_MAP = {
  name: "name",
  surname: "surname",
  nik: "displayed_name",
  email: "email",
  tel: "phone_number",
  country: "country",
  city: "city",
};
const REQUIRED = ["name", "surname", "email", "tel", "country", "city"];

const passwordScore = (v) => {
  let s = 0;
  if (v.length >= 8) s++;
  if (/[A-ZА-ЯЁ]/.test(v) && /[a-zа-яё]/.test(v)) s++;
  if (/\d/.test(v)) s++;
  if (/[^A-Za-zА-Яа-яЁё0-9]/.test(v)) s++;
  return s;
};

const PresonalInfo = () => {
  const { t } = useTranslation();
  const { me } = useMe();
  const kyc = useKycStatus();
  const { toast, notify } = useToast();

  const [form, setForm] = useState(EMPTY); // то, что сейчас в полях
  const [saved, setSaved] = useState(EMPTY); // то, что уже на сервере
  const [pw, setPw] = useState(EMPTY_PW);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!me) return;
    const next = {
      name: me.name ?? "",
      surname: me.surname ?? "",
      nik: me.displayed_name ?? "",
      email: me.email ?? "",
      tel: me.phone_number ?? "",
      country: me.country ?? "",
      city: me.city ?? "",
    };
    setForm(next);
    setSaved(next);
  }, [me]);

  const labels = {
    name: t("personalInfo.name"),
    surname: t("personalInfo.surName"),
    nik: t("personalInfo.nikName"),
    email: t("personalInfo.email"),
    tel: t("personalInfo.tel"),
    country: t("personalInfo.country"),
    city: t("personalInfo.town"),
  };

  const changedKeys = Object.keys(FIELD_MAP).filter((k) => form[k] !== saved[k]);
  const pwTouched = Boolean(pw.current || pw.next || pw.repeat);
  const dirty = changedKeys.length > 0 || pwTouched;

  const setField = (key) => (e) => {
    let value = e.target.value;
    if (key === "email") value = value.replace(/[а-яА-ЯёЁ]/g, ""); // как и раньше: кириллица в e-mail не допускается
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };
  const setPass = (key) => (e) => {
    setPw((p) => ({ ...p, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const discard = () => {
    setForm(saved);
    setPw(EMPTY_PW);
    setErrors({});
  };

  const save = async () => {
    if (saving || !dirty) return;

    const required = t("profile.required", "Обязательное поле");
    const nextErrors = {};
    REQUIRED.forEach((k) => {
      if (!String(form[k]).trim()) nextErrors[k] = required;
    });
    let pwMessage = "";
    if (pwTouched) {
      if (!pw.current) nextErrors.current = required;
      if (!pw.next) nextErrors.next = required;
      else if (pw.next !== pw.repeat) {
        pwMessage = t("profile.passwordMismatch", "Пароли не совпадают");
        nextErrors.repeat = pwMessage;
      }
    }
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      const hasEmpty = Object.values(nextErrors).some((m) => m === required);
      notify(
        `${t("kyc.aroseError")}: ${hasEmpty ? t("personalInfo.nullInputError") : pwMessage}`,
        "err"
      );
      return;
    }

    setSaving(true);
    const failures = [];
    const failedFields = {};
    const nextSaved = { ...saved };

    // отправляем только изменённые поля
    const results = await Promise.allSettled(
      changedKeys.map((k) =>
        axios.post(
          `${API}/users/edit`,
          { field: FIELD_MAP[k], value: form[k] },
          { headers: authHeaders() }
        )
      )
    );
    results.forEach((r, i) => {
      const k = changedKeys[i];
      if (r.status === "fulfilled") {
        nextSaved[k] = form[k];
      } else {
        const msg = extractError(r.reason, t("profile.unknownError", "Неизвестная ошибка"));
        failedFields[k] = msg;
        failures.push(`${labels[k]} — ${msg}`);
      }
    });

    if (pwTouched) {
      try {
        await axios.post(
          `${API}/users/password/change`,
          { previousPassword: pw.current, password: pw.next, repeatPassword: pw.repeat },
          { headers: authHeaders() }
        );
        setPw(EMPTY_PW);
      } catch (err) {
        const oldWrong = err?.response?.data?.detail === "Old password do not match";
        const msg = oldWrong
          ? t("profile.oldPasswordMismatch", "Старый пароль не совпадает")
          : t("profile.passwordError", "Ошибка при смене пароля");
        if (oldWrong) failedFields.current = msg;
        failures.push(msg);
      }
    }

    setSaved(nextSaved);
    setErrors(failedFields);
    setSaving(false);

    if (failures.length) notify(`${t("kyc.aroseError")}: ${failures.join("; ")}`, "err");
    else notify(t("personalInfo.success"), "ok");
  };

  const score = passwordScore(pw.next);
  const strengthText = !pw.next
    ? t("profile.pw.hint", "Используйте буквы разного регистра, цифры и символ")
    : score <= 1
    ? t("profile.pw.weak", "Слабый пароль")
    : score === 2
    ? t("profile.pw.medium", "Средний пароль")
    : score === 3
    ? t("profile.pw.good", "Хороший пароль")
    : t("profile.pw.great", "Отличный пароль");

  const matchHint = !pw.repeat
    ? null
    : pw.repeat === pw.next
    ? { text: t("profile.pw.match", "Пароли совпадают"), tone: "ok" }
    : { text: t("profile.passwordMismatch", "Пароли не совпадают"), tone: "warn" };

  // рельс должен сразу показывать сохранённые имя/e-mail
  const railMe = me
    ? { ...me, name: saved.name, surname: saved.surname, displayed_name: saved.nik, email: saved.email }
    : null;

  const savebar = (
    <div className={`fx-savebar${dirty ? " show" : ""}`} aria-hidden={!dirty}>
      <span>
        <i className="dot" />
        {t("profile.unsaved", "Есть несохранённые изменения")}
      </span>
      <button
        type="button"
        className="fx-btn fx-btn-primary"
        onClick={save}
        disabled={saving}
        tabIndex={dirty ? 0 : -1}
      >
        {saving ? t("profile.saving", "Сохраняем…") : t("profile.saveShort", "Сохранить")}
      </button>
    </div>
  );

  return (
    <ProfileLayout
      page="profile"
      header={<Header hasMenu2={true} />}
      me={railMe}
      kycStatus={kyc.status}
      toast={toast}
      floating={savebar}
    >
      <div className="fx-head">
        <h1>{t("profile.title", "Настройки профиля")}</h1>
        <p>{t("profile.subtitle", "Личные данные, контакты и параметры безопасности аккаунта.")}</p>
      </div>

      <VerifyCard id="verify" status={kyc.status} to={PROFILE_ROUTES.kyc} />

      {/* личные данные */}
      <section className="fx-section" id="personal">
        <div className="fx-section-head">
          <h2>{t("personalInfo.personalData")}</h2>
          <p>{t("profile.personalHint", "Используются для верификации аккаунта.")}</p>
        </div>
        <div className="fx-fgrid">
          <Field label={labels.name} error={errors.name}>
            <input type="text" autoComplete="given-name" value={form.name} onChange={setField("name")} aria-invalid={!!errors.name} />
          </Field>
          <Field label={labels.surname} error={errors.surname}>
            <input type="text" autoComplete="family-name" value={form.surname} onChange={setField("surname")} aria-invalid={!!errors.surname} />
          </Field>
          <Field full label={labels.nik} opt={t("profile.nikHint", "отображается другим пользователям")} error={errors.nik}>
            <input type="text" autoComplete="nickname" value={form.nik} onChange={setField("nik")} />
          </Field>
        </div>
      </section>

      {/* контакты */}
      <section className="fx-section" id="contacts">
        <div className="fx-section-head">
          <h2>{t("personalInfo.contact")}</h2>
          <p>{t("profile.contactsHint", "Используются для входа, уведомлений и восстановления доступа.")}</p>
        </div>
        <div className="fx-fgrid">
          <Field label={labels.email} icon={<IconMail />} error={errors.email}>
            <input type="email" autoComplete="email" value={form.email} onChange={setField("email")} aria-invalid={!!errors.email} />
          </Field>
          <Field label={labels.tel} icon={<IconPhone />} error={errors.tel}>
            <input type="tel" autoComplete="tel" placeholder="+7 900 000-00-00" value={form.tel} onChange={setField("tel")} aria-invalid={!!errors.tel} />
          </Field>
          <Field label={labels.country} icon={<IconPin />} error={errors.country}>
            <input type="text" autoComplete="country-name" value={form.country} onChange={setField("country")} aria-invalid={!!errors.country} />
          </Field>
          <Field label={labels.city} error={errors.city}>
            <input type="text" autoComplete="address-level2" value={form.city} onChange={setField("city")} aria-invalid={!!errors.city} />
          </Field>
        </div>
      </section>

      {/* безопасность */}
      <section className="fx-section" id="security">
        <div className="fx-section-head">
          <h2>{t("personalInfo.changePassword")}</h2>
          <p>{t("profile.securityHint", "Оставьте поля пустыми, если пароль менять не нужно.")}</p>
        </div>
        <div className="fx-fgrid">
          <Field full label={t("personalInfo.currentPassword")} error={errors.current}>
            <input type="password" autoComplete="current-password" placeholder="••••••••" value={pw.current} onChange={setPass("current")} aria-invalid={!!errors.current} />
          </Field>
          <Field
            label={t("personalInfo.newPassword")}
            error={errors.next}
            hint={strengthText}
            hintTone={score >= 3 && pw.next ? "ok" : undefined}
            extra={
              <div className={`fx-strength${pw.next ? ` s${Math.max(1, score)}` : ""}`} aria-hidden="true">
                <i /><i /><i /><i />
              </div>
            }
          >
            <input type="password" autoComplete="new-password" value={pw.next} onChange={setPass("next")} aria-invalid={!!errors.next} />
          </Field>
          <Field
            label={t("profile.repeatPassword", "Повторите пароль")}
            error={errors.repeat}
            hint={matchHint?.text}
            hintTone={matchHint?.tone}
          >
            <input type="password" autoComplete="new-password" value={pw.repeat} onChange={setPass("repeat")} aria-invalid={!!errors.repeat} />
          </Field>
        </div>
      </section>

      <div className="fx-actions">
        <button type="button" className="fx-btn fx-btn-primary" onClick={save} disabled={!dirty || saving}>
          {saving ? t("profile.saving", "Сохраняем…") : t("personalInfo.save")}
        </button>
        <button type="button" className="fx-btn fx-btn-ghost" onClick={discard} disabled={!dirty || saving}>
          {t("profile.cancel", "Отменить")}
        </button>
      </div>
    </ProfileLayout>
  );
};

export default PresonalInfo;
